import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import strings from 'strings';
import TextBox from 'UI/components/atoms/TextBox';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import { EntityType } from 'UI/constants/entityTypes';
import { idOptionSelected, REQUIRED_VALIDATION } from 'UI/utils';
import { OptionRenderers } from 'UI/utils/renderers';

import { DateTimePickers } from '../shared';

import {
  FORM_FIELDS_MAP,
  REACH_OUT_AUTOCOMPLETE_PROPS_BY_ENTITY
} from './RecruitJobOrder.constants';
import { useStyles } from './RecruitJobOrder.styles';

export const RecruitJobOrder = ({ entityType = null, isMultiple = false, disableTime = false }) => {
  const classes = useStyles();
  const { errors, register, unregister, watch, setValue } = useFormContext();

  const formValues = watch();

  const taskId = formValues[FORM_FIELDS_MAP.TaskId.key];

  const handleAutocompleteChange = (name, value) => {
    setValue(name, value, true);
  };

  const handleCommentsChange = (name, value) => {
    setValue(name, value);
  };

  useEffect(() => {
    register(FORM_FIELDS_MAP.Position.key, { ...REQUIRED_VALIDATION });
    if (!isMultiple) {
      register(FORM_FIELDS_MAP.ReachToName.key, { ...REQUIRED_VALIDATION });
    }

    return () => {
      unregister(FORM_FIELDS_MAP.Position.key);
      if (!isMultiple) {
        unregister(FORM_FIELDS_MAP.ReachToName.key);
      }
    };
  }, [isMultiple, register, unregister]);

  return (
    <Grid container spacing={2}>
      <Grid container item spacing={2}>
        <DateTimePickers disableTime={disableTime} />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle1" className={classes.fieldLabel}>
          <b>{strings.dailyPlan.drawer.form.labels.taskDetails}</b>
        </Typography>
        <AutocompleteSelect
          name={FORM_FIELDS_MAP.Position.key}
          error={!!errors[FORM_FIELDS_MAP.Position.key]}
          errorText={errors[FORM_FIELDS_MAP.Position.key]?.message}
          onSelect={handleAutocompleteChange}
          selectedValue={formValues[FORM_FIELDS_MAP.Position.key] || null}
          placeholder={strings.dailyPlan.drawer.form.placeholders.jobOrderPosition}
          renderOption={OptionRenderers.globalSearchDefault('title')}
          url="/search"
          typeahead
          typeaheadLimit={50}
          typeaheadParams={{ entityType: EntityType.Joborder }}
          getOptionSelected={idOptionSelected}
          disabled={entityType === EntityType.Joborder}
        />
      </Grid>

      <When condition={!isMultiple}>
        <Grid item>
          <Typography variant="body1" component="div" color="textSecondary">
            {strings.dailyPlan.drawer.form.labels.selectReachOutTo}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <AutocompleteSelect
            name={FORM_FIELDS_MAP.ReachToName.key}
            error={!!errors[FORM_FIELDS_MAP.ReachToName.key]}
            errorText={errors[FORM_FIELDS_MAP.ReachToName.key]?.message}
            onSelect={handleAutocompleteChange}
            selectedValue={formValues[FORM_FIELDS_MAP.ReachToName.key] || null}
            typeahead
            typeaheadLimit={50}
            disabled={entityType === EntityType.Candidate || entityType === EntityType.Contact}
            {...REACH_OUT_AUTOCOMPLETE_PROPS_BY_ENTITY.candidate}
          />
        </Grid>
      </When>

      <When condition={!taskId}>
        <Grid item xs={12}>
          <Typography variant="subtitle1">
            <b>{strings.dailyPlan.drawer.form.labels.comments}</b>
          </Typography>
          <TextBox
            label={strings.dailyPlan.drawer.form.labels.comments}
            placeholder={strings.dailyPlan.drawer.form.labels.comments}
            name={FORM_FIELDS_MAP.Comments.key}
            onChange={handleCommentsChange}
            value={formValues[FORM_FIELDS_MAP.Comments.key]}
            inputRef={register()}
            multiline
          />
        </Grid>
      </When>
    </Grid>
  );
};
